/**
 * <Reveal> — un contenu qui s'ouvre et se referme en douceur (atom, `proposé`).
 *
 * La hauteur passe de rien à celle du contenu (et l'inverse) à la durée `standard` et à la courbe de la
 * marque : ce qui est dessous glisse au lieu de sauter. Né pour le détail d'une barre du live (« Connexion
 * faible » touchée, daily du 23/10 : le détail s'ouvrait d'un coup, la visio sautait d'autant, et Sam avec
 * elle). Refermé, le contenu n'est plus monté ; en mouvement réduit, il est aussitôt là ou parti.
 *
 * Pendant le mouvement, le contenu se mesure à sa hauteur naturelle, posé en haut de la boîte qui le découpe :
 * la boîte seule s'anime, par le pilote JS (une hauteur ne passe pas par le pilote natif). Ouvert et arrivé, il
 * reprend sa place dans la mise en page : ce qui grandit dedans (un second `<Reveal>`) n'a rien à remesurer.
 *
 * Contrôlé : `open` arrive, l'appelant le change.
 */
import React, { useMemo, useState } from "react"
import { Animated, View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { usePresence } from "../../hooks/usePresence"

export type RevealProps = {
    open: boolean
    children: React.ReactNode
    testID?: string
}

export function Reveal(props: RevealProps) {
    const { open, children, testID } = props
    const { progress, mounted, rested } = usePresence(open)
    // État d'interface : la hauteur naturelle du contenu, d'où la boîte part en se refermant.
    const [contentHeight, setContentHeight] = useState(0)

    const height = useMemo(
        () => progress.interpolate({ inputRange: [0, 1], outputRange: [0, contentHeight] }),
        [progress, contentHeight]
    )

    return (
        <Animated.View style={[styles.clip, rested ? undefined : { height }]} testID={testID} aria-hidden={!open}>
            {mounted && (
                <View
                    style={rested ? undefined : styles.moving}
                    onLayout={(event) => setContentHeight(event.nativeEvent.layout.height)}
                >
                    {children}
                </View>
            )}
        </Animated.View>
    )
}

const styles = StyleSheet.create({
    clip: { overflow: "hidden" },
    /** En mouvement : à sa hauteur naturelle, en haut de la boîte qui le découpe. */
    moving: { position: "absolute", top: 0, left: 0, right: 0 },
})
